// The survey of the viewer's faction, as the client keeps it (v2-the-deep.md
// §4.1, §4.2, §14.9, R2611, R2626).
//
// The hello carries the whole bitset (`Hello.survey`, bit c%8 of byte c/8 is
// cell c, the low bit first) and every known point of interest
// (`Hello.pois`). A State delta carries the cells of the last survey pass
// (`State.surveyed`, in cell order) with their bands and their currents
// (`surveyed_bands`, `surveyed_currents`) and the points those cells hold
// (`State.pois`). A full frame restates the whole bitset in `State.survey`
// and every known point, as it restates the owners, and it carries in
// `surveyed` the bands and the currents of the cells the picture lacks: the
// cells of a dropped delta (R2633).
//
// **NO BITSET IS NO SURVEY.** Every field is empty while the flag
// `discovery` is off, so `on` stays false, `has` answers true for every
// cell, and a reader draws the map of today (v2 §14.1 rule 1). A canvas
// layer reads `on` and the bits, never a flag (rule 5).
//
// Everything here is pure data, so a test reads it with no engine.

import type { Hello, PoiLive, PointOfInterest, State } from './gen/proto/aquaphobia';
import { PoiKind } from './gen/proto/aquaphobia';

/** Is cell `c` set in a bitset of the wire's encoding? */
export function surveyBit(bits: Uint8Array, c: number): boolean {
  const i = c >> 3;
  return c >= 0 && i < bits.length && (bits[i] & (1 << (c & 7))) !== 0;
}

/** How many cells a bitset holds. */
export function surveyCount(bits: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < bits.length; i++) {
    let b = bits[i];
    while (b !== 0) { b &= b - 1; n++; }
  }
  return n;
}

/** What one State frame changed in the survey. */
export interface SurveyDelta {
  /** The frame restated the whole bitset: every cell may have changed. */
  full: boolean;
  /** The cells new to the survey, in cell order. Empty on a full frame. */
  cells: readonly number[];
  /** The points of interest new to the store, in id order. A full frame
   *  names the points the store did not hold before it. */
  pois: readonly PointOfInterest[];
}

/** The four kinds of point that phase 3 shows (§4.2): a seeded cache waits
 *  for phase 5, so the store keeps it and no reader draws it. */
export const SHOWN_POI_KINDS: readonly PoiKind[] = [
  PoiKind.POI_KIND_VENT, PoiKind.POI_KIND_RUIN, PoiKind.POI_KIND_GATE, PoiKind.POI_KIND_LAIR,
];

/** Does phase 3 show a point of this kind? */
export function poiShown(kind: PoiKind): boolean {
  return SHOWN_POI_KINDS.includes(kind);
}

/** One live row of a point and the wall clock of the frame that brought
 *  it, so a reader counts `left_sec` down from there. */
export interface PoiLiveSeen {
  row: PoiLive;
  atMs: number;
}

/**
 * The live state of the points of interest (R2735, v2-the-deep.md §14.9):
 * the last `State.poi_live` row the client saw of each point. The server
 * sends a row only while the commander hears the cell of the point, so
 * the store keeps a row after the hearing stops, a full frame included,
 * and the chart draws the last state the client saw. A new hello starts
 * it empty.
 */
export class PoiLiveStore {
  private readonly byId = new Map<number, PoiLiveSeen>();
  private revV = 0;

  /** A counter that moves on every change, so a reader packs again. */
  get rev(): number { return this.revV; }

  /** The last row of a point, or undefined before the client heard it. */
  get(id: number): PoiLiveSeen | undefined { return this.byId.get(id); }

  /** Takes the rows of one State frame; answers whether one came. */
  apply(rows: readonly PoiLive[] | undefined, nowMs: number): boolean {
    if (!rows || rows.length === 0) return false;
    for (const row of rows) this.byId.set(row.id, { row, atMs: nowMs });
    this.revV++;
    return true;
  }

  /** Forgets every row: a new picture of the world. */
  clear(): void {
    this.byId.clear();
    this.revV++;
  }
}

export class SurveyStore {
  /** The live state of the known points (R2735). */
  readonly live = new PoiLiveStore();
  /** The bitset, or null while the wire carries none (`discovery` off). */
  private bitsV: Uint8Array | null = null;
  private readonly byId = new Map<number, PointOfInterest>();
  private readonly byCell = new Map<number, PointOfInterest>();
  private listV: PointOfInterest[] = [];
  private revV = 0;

  /** True while the wire carries a survey. */
  get on(): boolean { return this.bitsV !== null; }

  /** The bitset, or null while the wire carries none. Readers must not
   *  write it. */
  get bits(): Uint8Array | null { return this.bitsV; }

  /** A counter that moves on every change, so a reader packs again. */
  get rev(): number { return this.revV; }

  /** Every known point of interest, in id order. */
  get pois(): readonly PointOfInterest[] { return this.listV; }

  /** Does the survey hold a cell? Every cell reads surveyed while the wire
   *  carries no survey, so a world with the flag off shows the map of
   *  today. */
  has(cell: number): boolean {
    return this.bitsV === null || surveyBit(this.bitsV, cell);
  }

  /** The known point of interest on a cell, or undefined. */
  poiAt(cell: number): PointOfInterest | undefined { return this.byCell.get(cell); }

  /** The known point of interest with an id, or undefined. */
  poi(id: number): PointOfInterest | undefined { return this.byId.get(id); }

  /** The share of the cells the survey holds, 0 to 1, or 0 with none. */
  share(cells: number): number {
    return this.bitsV && cells > 0 ? surveyCount(this.bitsV) / cells : 0;
  }

  /** Takes the survey of a hello: a new picture of the world. */
  fromHello(h: Pick<Hello, 'survey' | 'pois'>): void {
    this.bitsV = h.survey && h.survey.length > 0 ? h.survey.slice() : null;
    this.live.clear();
    this.byId.clear();
    this.byCell.clear();
    this.listV = [];
    for (const p of h.pois ?? []) this.add(p);
    this.sortList();
    this.revV++;
  }

  /**
   * Takes the survey of one State frame. Answers what changed, or null for
   * a frame that changed nothing, which is every frame while the flag is
   * off.
   */
  applyState(s: Pick<State, 'full' | 'survey' | 'surveyed' | 'pois'>): SurveyDelta | null {
    if (s.full) {
      // A full frame of an arcade match carries the lairs of its map and
      // no survey: the map is public (arcade.md §20.4, R3216). The points
      // stand, and the survey stays off.
      const bitsIn = !!s.survey && s.survey.length > 0;
      if (!bitsIn && (s.pois ?? []).length === 0) return null;
      if (bitsIn) this.bitsV = s.survey!.slice();
      const fresh: PointOfInterest[] = [];
      for (const p of s.pois ?? []) if (!this.byId.has(p.id)) fresh.push(p);
      this.byId.clear();
      this.byCell.clear();
      this.listV = [];
      for (const p of s.pois ?? []) this.add(p);
      this.sortList();
      this.revV++;
      return { full: true, cells: [], pois: fresh };
    }
    const cells = s.surveyed ?? [];
    const pois = s.pois ?? [];
    if (cells.length === 0 && pois.length === 0) return null;
    const bits = this.bitsV;
    // A delta before any bitset is a wire the hello did not open: keep
    // nothing rather than invent a survey of one pass.
    if (bits === null) return null;
    for (const c of cells) {
      const i = c >> 3;
      if (i < bits.length) bits[i] |= 1 << (c & 7);
    }
    const fresh: PointOfInterest[] = [];
    for (const p of pois) {
      if (this.byId.has(p.id)) continue;
      this.add(p);
      fresh.push(p);
    }
    if (fresh.length > 0) this.sortList();
    this.revV++;
    return { full: false, cells, pois: fresh };
  }

  private add(p: PointOfInterest): void {
    this.byId.set(p.id, p);
    this.byCell.set(p.cell, p);
    this.listV.push(p);
  }

  private sortList(): void {
    this.listV.sort((a, b) => a.id - b.id);
  }
}

/**
 * Clears the band and the current of every cell that a full frame's bitset
 * does not hold (R2825). The hello carries 0 there, so a field the client
 * grew stays the field of the hello. A full frame can take cells away: the
 * frame after TOGGLE VEIL of an admin seat goes off restates the real
 * survey. Answers whether a value moved.
 */
export function clearUnsurveyed(bits: Uint8Array, depth: Uint8Array | null, field: Int8Array | null): boolean {
  let moved = false;
  if (depth) {
    for (let c = 0; c < depth.length; c++) {
      if (depth[c] !== 0 && !surveyBit(bits, c)) { depth[c] = 0; moved = true; }
    }
  }
  if (field) {
    for (let c = 0; 2 * c + 1 < field.length; c++) {
      if ((field[2 * c] !== 0 || field[2 * c + 1] !== 0) && !surveyBit(bits, c)) {
        field[2 * c] = 0;
        field[2 * c + 1] = 0;
        moved = true;
      }
    }
  }
  return moved;
}

/**
 * Writes the bands and the currents of the new cells of a delta into the
 * fields of the hello (R2616), which the client keeps as its own copies. A
 * band array or a current array of another length than the cells is left
 * out: the flag of that field is off.
 */
export function mergeSurveyed(
  cells: readonly number[], bands: Uint8Array | undefined, currents: Uint8Array | undefined,
  depth: Uint8Array | null, field: Int8Array | null,
): boolean {
  let moved = false;
  if (depth && bands && bands.length === cells.length) {
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (c < depth.length && depth[c] !== bands[i]) { depth[c] = bands[i]; moved = true; }
    }
  }
  if (field && currents && currents.length === 2 * cells.length) {
    const pairs = new Int8Array(currents.buffer, currents.byteOffset, currents.length);
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (2 * c + 1 >= field.length) continue;
      if (field[2 * c] !== pairs[2 * i] || field[2 * c + 1] !== pairs[2 * i + 1]) {
        field[2 * c] = pairs[2 * i];
        field[2 * c + 1] = pairs[2 * i + 1];
        moved = true;
      }
    }
  }
  return moved;
}
